<template>
  <div class="showcase-editor">
    <FoilShowcase
      :slots="player.showcase"
      :empty-text="t('profile.showcase.emptyOwn')"
      interactive
      @pick="openAt"
    />

    <div class="slot-row">
      <button
        v-for="index in PROFILE_SHOWCASE_LIMIT"
        :key="index"
        type="button"
        class="slot-chip"
        :class="{ on: player.showcase[index - 1] }"
        @click="openAt(index - 1)"
      >
        {{ index }}
      </button>
    </div>
    <p class="hint">{{ t('profile.showcase.editHint') }}</p>

    <div
      v-if="picking !== null"
      class="picker-scrim"
      @click.self="picking = null"
    >
      <div class="picker" role="dialog" :aria-label="t('profile.showcase.pick')">
        <header class="picker-head">
          <h3>{{ t('profile.showcase.pick') }}</h3>
          <button type="button" class="ghost-btn" @click="picking = null">
            {{ t('mobile.close') }}
          </button>
        </header>
        <label class="visually-hidden" for="showcase-search">
          {{ t('profile.showcase.search') }}
        </label>
        <input
          id="showcase-search"
          v-model="query"
          class="search"
          type="search"
          :placeholder="t('profile.showcase.search')"
          autocomplete="off"
        />
        <p v-if="ownedPrints.length === 0" class="hint">
          {{ t('profile.showcase.needAlbum') }}
        </p>
        <div v-else class="picker-body">
          <div ref="gridEl" class="print-grid">
          <button
            v-if="player.showcase[picking]"
            type="button"
            class="print-card clear"
            @click="clearSlot"
          >
            {{ t('profile.showcase.clear') }}
          </button>
          <button
            v-for="print in visiblePrints"
            :key="`${print.cardId}:${print.foil}`"
            type="button"
            class="print-card"
            :class="{ on: isCurrent(print) }"
            :disabled="!canPlace(print)"
            :title="canPlace(print) ? undefined : t('profile.showcase.allPinned')"
            @click="choose(print)"
          >
            <CardArt
              :card-id="print.cardId"
              :foil="print.foil"
              :alt="print.name"
              intensity="thumb"
            />
            <span class="print-name">{{ print.name }}</span>
            <span class="print-foil">
              {{ t(`packs.rarity.${print.foil}`) }}
              <template v-if="print.owned > 1"> ×{{ print.owned }}</template>
            </span>
          </button>
        </div>
        <nav
          v-if="pageCount > 1"
          class="pager"
          :aria-label="pageLabel"
        >
          <button
            type="button"
            class="ghost-btn"
            :disabled="page === 0"
            @click="stepPage(-1)"
          >
            {{ t('profile.showcase.prev') }}
          </button>
          <span class="page-status">{{ pageLabel }}</span>
          <button
            type="button"
            class="ghost-btn"
            :disabled="page >= pageCount - 1"
            @click="stepPage(1)"
          >
            {{ t('profile.showcase.next') }}
          </button>
        </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  CARD_FOIL_IDS_BEST_FIRST,
  CARD_FOIL_RANK,
  PROFILE_SHOWCASE_LIMIT,
  type CardFoilId,
  type ProfileShowcaseSlot,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { usePlayerStore } from '../stores/player'
import { intlLocale } from '../stores/locale'
import { putShowcase } from '../api/profile'
import { allCatalogCards } from '../game/packs/packCatalog'
import CardArt from './CardArt.vue'
import FoilShowcase from './FoilShowcase.vue'

interface OwnedPrint {
  cardId: string
  foil: CardFoilId
  name: string
  owned: number
}

const PAGE_SIZE = 36

const { t, locale } = useI18n()
const player = usePlayerStore()
const collection = useCollectionStore()
const auth = useAuthStore()

const picking = ref<number | null>(null)
const query = ref('')
const page = ref(0)
const gridEl = ref<HTMLElement | null>(null)
let persistTimer: ReturnType<typeof setTimeout> | null = null

const ownedPrints = computed<OwnedPrint[]>(() => {
  const out: OwnedPrint[] = []
  for (const card of allCatalogCards()) {
    for (const foil of CARD_FOIL_IDS_BEST_FIRST) {
      const owned = collection.foilCount(card.id, foil)
      if (owned < 1) continue
      out.push({
        cardId: card.id,
        foil,
        name: localizedName(card.id),
        owned,
      })
    }
  }
  const collator = intlLocale(locale.value)
  out.sort((a, b) => {
    const byName = a.name.localeCompare(b.name, collator, { sensitivity: 'base' })
    if (byName !== 0) return byName
    return CARD_FOIL_RANK[a.foil] - CARD_FOIL_RANK[b.foil]
  })
  return out
})

const filteredPrints = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return ownedPrints.value
  return ownedPrints.value.filter(
    (print) =>
      print.name.toLowerCase().includes(q) ||
      print.cardId.includes(q) ||
      print.foil.includes(q),
  )
})

const pageCount = computed(() =>
  Math.max(1, Math.ceil(filteredPrints.value.length / PAGE_SIZE)),
)

const visiblePrints = computed(() => {
  const start = page.value * PAGE_SIZE
  return filteredPrints.value.slice(start, start + PAGE_SIZE)
})

const pageLabel = computed(() => {
  const list = visiblePrints.value
  const first = list[0]
  const last = list[list.length - 1]
  if (!first || !last) return ''
  return t('profile.showcase.page', {
    from: pageInitial(first.name),
    to: pageInitial(last.name),
    page: page.value + 1,
    pages: pageCount.value,
  })
})

function pageInitial(name: string): string {
  const first = Array.from(name.trim())[0] ?? ''
  return first.toLocaleUpperCase(intlLocale(locale.value))
}

function scrollGridTop(): void {
  void nextTick(() => {
    if (gridEl.value) gridEl.value.scrollTop = 0
  })
}

function stepPage(delta: number): void {
  const next = page.value + delta
  if (next < 0 || next > pageCount.value - 1) return
  page.value = next
}

watch(query, () => {
  page.value = 0
  scrollGridTop()
})

watch(page, () => {
  scrollGridTop()
})

watch(pageCount, (count) => {
  if (page.value > count - 1) page.value = Math.max(0, count - 1)
})

function openAt(index: number): void {
  picking.value = Math.max(0, Math.min(PROFILE_SHOWCASE_LIMIT - 1, index))
  query.value = ''
  page.value = 0
}

function isCurrent(print: OwnedPrint): boolean {
  const slot = picking.value === null ? null : player.showcase[picking.value]
  return slot?.cardId === print.cardId && slot.foil === print.foil
}

/** Copies of this print already sitting in slots other than the one being edited. */
function pinnedElsewhere(print: OwnedPrint): number {
  const index = picking.value
  return player.showcase.filter(
    (slot, i) =>
      i !== index && slot.cardId === print.cardId && slot.foil === print.foil,
  ).length
}

function canPlace(print: OwnedPrint): boolean {
  if (isCurrent(print)) return true
  return print.owned - pinnedElsewhere(print) > 0
}

function choose(print: OwnedPrint): void {
  if (picking.value === null || !canPlace(print)) return
  const index = picking.value
  const next = player.showcase.slice()
  const slot: ProfileShowcaseSlot = { cardId: print.cardId, foil: print.foil }
  if (index < next.length) next[index] = slot
  else next.push(slot)
  player.setShowcase(next.slice(0, PROFILE_SHOWCASE_LIMIT))
  picking.value = null
  queuePersist()
}

function clearSlot(): void {
  if (picking.value === null) return
  const next = player.showcase.slice()
  next.splice(picking.value, 1)
  player.setShowcase(next)
  picking.value = null
  queuePersist()
}

function queuePersist(): void {
  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    persistTimer = null
    const token = auth.token
    if (!token) return
    void putShowcase(token, player.showcase).then((payload) => {
      player.setShowcase(payload.showcase)
    }).catch(() => undefined)
  }, 280)
}
</script>

<style scoped>
.hint {
  margin: 12px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.slot-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.slot-chip,
.ghost-btn,
.print-card {
  font: inherit;
  cursor: pointer;
}

.slot-chip {
  width: 36px;
  height: 36px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 999px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  font-weight: 700;
}

.slot-chip.on {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  border-color: transparent;
}

.picker-scrim {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: end center;
  padding: 12px;
  background: rgba(8, 6, 12, 0.62);
}

.picker {
  width: min(720px, 100%);
  max-height: min(86vh, 720px);
  display: flex;
  flex-direction: column;
  padding: 14px 14px max(18px, env(safe-area-inset-bottom));
  border-radius: 16px 16px 12px 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-3, 0 16px 40px rgba(0, 0, 0, 0.4));
}

.picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.picker-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.ghost-btn {
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 8px;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  font-size: 13px;
}

.ghost-btn:disabled {
  opacity: 0.38;
  cursor: default;
}

.picker-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 10px;
}

.pager .ghost-btn {
  min-height: 44px;
}

.page-status {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--md-sys-color-on-surface-variant);
}

.search {
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
}

.print-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 10px;
  overflow: auto;
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 4px;
}

.print-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 6px 10px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 10px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
}

.print-card.on {
  border-color: var(--md-sys-color-primary);
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.print-card:disabled {
  opacity: 0.42;
  cursor: default;
}

.print-card.clear {
  justify-content: center;
  min-height: 120px;
  font-size: 13px;
  font-weight: 600;
}

.print-name,
.print-foil {
  text-align: center;
  line-height: 1.2;
}

.print-name {
  font-size: 11px;
  font-weight: 600;
}

.print-foil {
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--md-sys-color-on-surface-variant);
}

.print-card.on .print-foil {
  color: inherit;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (min-width: 721px) {
  .picker-scrim {
    place-items: center;
  }

  .picker {
    border-radius: 16px;
  }
}
</style>
